import { Box } from "@mui/material";
import { Alert } from "@platform/design-system";

import { Loader } from "../../components";
import { extractErrorMessage } from "../../utils";

import { DecidedZoneCard } from "./stewardship/DecidedZoneCard";
import { toTwoZoneMetrics } from "./stewardship/twoZoneMetrics";
import { UndecidedZoneCard } from "./stewardship/UndecidedZoneCard";

import type { ConsultingBrokerStewardshipResponse } from "./types";

type Metrics = ConsultingBrokerStewardshipResponse["metrics"];

const EMPTY_LABEL = "No preferred-carrier activity for the selected period.";

function hasPreferredCarrierData(metrics: Metrics): boolean {
  // The per-broker stewardship endpoint runs aggregate queries that return
  // zero-filled migration/retention/pending objects (never null) when there is
  // no activity, so emptiness must be judged on the values feeding the zones —
  // not on the presence of the objects.
  const { decided, undecided } = toTwoZoneMetrics(metrics);

  return Boolean(
    decided.movedOntoPreferred.premium ||
    decided.keptOnPreferred.premium ||
    decided.movedToNonPreferred.premium ||
    undecided.inFlightTotal ||
    undecided.toWin ||
    undecided.toDefend ||
    undecided.groupsPending,
  );
}

type PreferredCarrierZonesProps = {
  metrics?: Metrics;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
};

function PreferredCarrierZones({
  metrics,
  isLoading,
  isError,
  error,
}: PreferredCarrierZonesProps): React.JSX.Element {
  return (
    <Box data-testid="preferred-carrier-zones">
      {renderZones({ metrics, isLoading, isError, error })}
    </Box>
  );
}

function renderZones({
  metrics,
  isLoading,
  isError,
  error,
}: PreferredCarrierZonesProps): React.JSX.Element {
  if (isError) {
    return <Alert severity="error">{extractErrorMessage(error)}</Alert>;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (!metrics || !hasPreferredCarrierData(metrics)) {
    return <Alert severity="info">{EMPTY_LABEL}</Alert>;
  }

  const { decided, undecided } = toTwoZoneMetrics(metrics);

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <DecidedZoneCard decided={decided} />
      <UndecidedZoneCard undecided={undecided} />
    </Box>
  );
}

export { PreferredCarrierZones };
